import React, { useEffect, useMemo, useState } from 'react';
import { Input, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import SimpleBar from "simplebar-react";
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { updateEventSong } from "../../slices/event/song/edit/thunk";
import { eventSongEditReset } from "../../slices/event/song/edit/reducer";
import { viewEventSongs } from "../../slices/event/view/thunk";
import { viewSongs } from "../../slices/song/thunk";
import { notificationService } from "../../helpers/NotificationService";
import { normalizeSongText, songQueryTokens, matchesSongQuery, songMatchRank } from "../../helpers/songSearch";

const MAX_RESULTS = 50;

/**
 * Swaps the song of a request the guest already has on the list, keeping its place
 * in the queue and its dedication. The picker mirrors the add modal: search the
 * catalogue, or type a song of your own when the host allows it.
 */
export const EditEventSongModal = ({ eventSong, event, eventId, guestId, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");

  const loadFullDatabase = !!event?.loadFullSongDatabase;
  const canAddFromOutside = event?.allowGuestsAddSongsFromDatabaseOutside !== false;

  const selectSongList = createSelector(
    (state) => state.SongList,
    (slice) => ({ songList: slice.list })
  );
  const { songList } = useSelector(selectSongList);

  const selectEventSongEdit = createSelector(
    (state) => state.EventSongEdit,
    (slice) => ({
      editSuccess: slice.success,
      editError: slice.errorMessage,
      editSaving: slice.saving,
    })
  );
  const { editSuccess, editError, editSaving } = useSelector(selectEventSongEdit);

  useEffect(() => {
    if (eventSong) {
      setSearch("");
      dispatch(viewSongs(event?.id || null));
    }
  }, [eventSong]);

  useEffect(() => {
    if (editSuccess) {
      dispatch(eventSongEditReset());
      dispatch(viewEventSongs(eventId));
      onClose();
    }
  }, [editSuccess]);

  useEffect(() => {
    if (editError) {
      notificationService.error(t(editError));
      dispatch(eventSongEditReset());
    }
  }, [editError]);

  const normalizedSongList = useMemo(
    () => songList.map((s) => ({ song: s, normalized: normalizeSongText(s.song) })),
    [songList]
  );

  const filteredSongs = useMemo(() => {
    if (!search.trim()) {
      return loadFullDatabase ? songList.slice(0, MAX_RESULTS) : [];
    }
    const nv = normalizeSongText(search);
    const tokens = songQueryTokens(search);

    return normalizedSongList
      .filter((entry) => matchesSongQuery(entry.normalized, tokens))
      .sort((a, b) => songMatchRank(a.normalized, nv) - songMatchRank(b.normalized, nv))
      .slice(0, MAX_RESULTS)
      .map((entry) => entry.song);
  }, [search, songList, normalizedSongList, loadFullDatabase]);

  const hasExactMatch = useMemo(() => {
    if (!search.trim()) return false;
    const nv = normalizeSongText(search);
    return normalizedSongList.some((entry) => entry.normalized === nv);
  }, [search, normalizedSongList]);

  const showOutsideRow = canAddFromOutside && search.trim().length > 0 && !hasExactMatch;
  const showNotFound = !canAddFromOutside && search.trim().length > 0 && filteredSongs.length === 0;

  // Backend returns title/artist separately; the fallback splits "Artist - Title".
  const songTitle = (s) => s.title ?? (s.song?.includes(' - ') ? s.song.split(' - ').slice(1).join(' - ') : s.song);
  const songArtist = (s) => s.artist ?? (s.song?.includes(' - ') ? s.song.split(' - ')[0] : '');

  const handlePick = (songPart) => {
    if (editSaving) return;
    dispatch(updateEventSong(eventSong.id, guestId, songPart));
  };

  const renderRow = (key, title, artist, songPart) => (
    <div key={key}>
      <div
        className="event-song-row song ps-2 p-1"
        style={{ cursor: 'pointer' }}
        onClick={() => handlePick(songPart)}
      >
        <div className="event-song-text">
          <div className="text-container-mobile fw-medium">{title}</div>
          {artist && <div className="text-container-mobile event-song-artist text-muted mb-0">{artist}</div>}
        </div>
        <button type="button" className="btn btn-sm btn-icon text-white flex-shrink-0 ms-2" title={t('Save')}>
          <i className="ri-check-line fs-20"></i>
        </button>
      </div>
    </div>
  );

  return (
    <Modal isOpen={!!eventSong} toggle={onClose}>
      <ModalHeader toggle={onClose}>{t('Change song')}</ModalHeader>
      <ModalBody>
        <div className="mb-2 text-muted">
          {t('Currently on the list')}: <span className="fw-medium">{eventSong?.title} - {eventSong?.artist}</span>
        </div>
        <div className="app-search p-0 mb-2">
          <div className="position-relative">
            <Input
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              type="text"
              className="form-control form-control-lg"
              placeholder={t("Search and add a song")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ backgroundImage: 'none' }}
            />
            <span className="mdi mdi-magnify search-widget-icon"></span>
          </div>
        </div>

        <SimpleBar style={{ maxHeight: '50vh' }}>
          <div className="event-song-list event-song-list-search list">
            {showOutsideRow && renderRow('outside', search.trim(), null, { id: null, artistTitle: search.trim() })}

            {filteredSongs.map((s) => renderRow(`db-${s.id}`, songTitle(s), songArtist(s), { id: s.id }))}

            {showNotFound && (
              <div className="text-center text-muted py-4">
                <i className="ri-search-line d-block mb-2" style={{ fontSize: '2rem' }}></i>
                {t('Not found song')}
              </div>
            )}

            {!search.trim() && !loadFullDatabase && filteredSongs.length === 0 && (
              <div className="text-center text-muted py-4">
                <i className="ri-music-2-line d-block mb-2" style={{ fontSize: '2rem' }}></i>
                {t('Search For a Song')}
              </div>
            )}
          </div>
        </SimpleBar>
      </ModalBody>
      <ModalFooter>
        <div className="hstack gap-2 justify-content-end">
          <button type="button" className="btn btn-label btn-light" onClick={onClose}>
            <i className="ri-close-circle-line label-icon align-middle fs-16 me-2"></i>{t('Close')}
          </button>
        </div>
      </ModalFooter>
    </Modal>
  );
};
